<template>
  <TransitionRoot as="template" :show="isOpen">
    <Dialog ref="dialog" as="div" class="relative z-[999]" @close="onClose">
      <div class="fixed inset-0 top-0 overflow-hidden">
        <div class="absolute inset-0 top-0 overflow-hidden">
          <div
            class="pointer-events-none fixed bottom-0 right-0 top-0 flex max-w-full"
          >
            <TransitionChild
              as="template"
              enter="transform transition ease-in-out duration-250"
              enter-from="translate-x-full"
              enter-to="translate-x-0"
              leave="transform transition ease-in-out duration-250"
              leave-from="translate-x-0"
              leave-to="translate-x-full"
              @after-enter="onAfterEnter"
            >
              <DialogPanel
                class="pointer-events-auto relative w-screen md:max-w-[500px]"
              >
                <div
                  class="flex h-full flex-col overflow-auto bg-primary shadow-xl"
                >
                  <div
                    class="border-b-white flex items-center justify-between border-b bg-primary px-4 py-3 sticky top-0 z-1"
                  >
                    <div class="flex flex-wrap gap-4">
                      <div
                        class="ui-text-xs text-white capitalize underline-offset-2 cursor-pointer"
                        :class="{
                          underline: activeTab === tabIds.productDetails,
                        }"
                        @click="activeTab = tabIds.productDetails"
                      >
                        {{ $t("productDetails") }}
                      </div>
                      <div
                        v-if="shippingReturnsAccordionData"
                        class="ui-text-xs text-white underline-offset-2 cursor-pointer"
                        :class="{
                          underline: activeTab === tabIds.shippingAndReturns,
                        }"
                        @click="activeTab = tabIds.shippingAndReturns"
                      >
                        {{ shippingReturnsAccordionData.title }}
                      </div>
                    </div>
                    <UiButton
                      variant-type="icon"
                      variant="clear"
                      size="xs"
                      :aria-label="
                        $t('ariaLabelClose', { title: $t('productDetails') })
                      "
                      @click="onClose"
                    >
                      <UiIcon name="close" class="size-6 text-white" />
                    </UiButton>
                  </div>
                  <div class="px-4 py-8">
                    <ProductDetailsInfo
                      v-if="activeTab === tabIds.productDetails"
                      :details-map="detailsMapedData"
                    />
                    <div
                      v-if="
                        activeTab === tabIds.shippingAndReturns &&
                        shippingReturnsAccordionData
                      "
                      class="space-y-4"
                    >
                      <div class="ui-h2 text-white">
                        {{ shippingReturnsAccordionData.title }}
                      </div>
                      <div
                        class="ui-text-sm text-white"
                        v-html="shippingReturnsAccordionData.content"
                      />
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { IProductPdpInfoProps } from "ecom-types";

const props = defineProps<
  Pick<
    IProductPdpInfoProps,
    "detailsMapedData" | "shippingReturnsAccordionData"
  > & {
    modelValue: boolean;
  }
>();

const emit = defineEmits(["update:modelValue"]);

const generalInternalStore = useGeneralInternalStore();

const tabIds = {
  productDetails: 1,
  shippingAndReturns: 2,
};

const activeTab = ref(tabIds.productDetails);

const isOpen = computed<boolean>({
  set(val) {
    emit("update:modelValue", val);
  },
  get() {
    return props.modelValue;
  },
});

watch(
  () => props.modelValue,
  (val) => {
    generalInternalStore.systemOverlay = val;
  },
);

function onAfterEnter() {
  setTimeout(() => {
    const inertEl = document.querySelector("[inert]");
    if (inertEl) {
      inertEl.removeAttribute("inert");
    }
  }, 0);
}

function onClose() {
  generalInternalStore.systemOverlay = false;
  isOpen.value = false;
  activeTab.value = tabIds.productDetails;
}
</script>
